import Avatar from '-/Custom/Avatar';
import * as Header from '-/Custom/Header';
import { Link, Stack, useLocalSearchParams, useRouter } from 'expo-router';
import type { ResultOf, VariablesOf } from 'gql.tada';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { Platform, ScrollView } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import { useMutation, useQuery } from 'urql';
import FormSaveButton from '@/components/Custom/FormSaveButton';
import ModalKeyboardStickyView from '@/components/Custom/ModalKeyboardStickyView';
import {
  PersonListItemAvatarFragment,
  PersonListItemNameFragment,
} from '@/components/Person/PersonListItem';
import ActionButton from '@/components/UI/ActionButton';
import Box from '@/components/UI/Box';
import ChipPreferenceRow, {
  type ChipPreferenceOption,
} from '@/components/UI/ChipPreferenceRow';
import ContentBoundary from '@/components/UI/ContentBoundary';
import {
  NOTIFICATION_PREFERENCE_OPTIONS,
  type NotificationPreferenceChipValue,
} from '@/components/UI/NotificationPreference.options';
import Row from '@/components/UI/Row';
import Text from '@/components/UI/Text';
import { graphql, readFragment } from '@/gql/graphql';
import useHaptics from '@/hooks/useHaptics';
import useHeaderBackButton from '@/hooks/useHeaderBackButton';
import useUserId from '@/hooks/useUserId';
import getAvatarText from '@/utils/getAvatarText';

const GetChatDetailInfo = graphql(/* GraphQL */ `
  query GetChatDetailInfo($chatId: uuid!) {
    current_user {
      id
      chat_notification_preference
    }
    chat_by_pk(id: $chatId) {
      id
      chat_type
      title
      avatar_id
      color
      participants {
        id
        ...PersonListItemAvatar
        ...PersonListItemName
      }
      is_hidden
      preference {
        chat_id
        person_id
        notification_preference
      }
      metadata
    }
  }
`, [PersonListItemAvatarFragment, PersonListItemNameFragment]);

const SaveChatPreferences = graphql(/* GraphQL */ `
  mutation SaveChatPreferences(
    $object: chat_person_preference_insert_input!
    $updateColumns: [chat_person_preference_update_column!]!
  ) {
    insert_chat_person_preference_one(
      object: $object
      on_conflict: {
        constraint: chat_person_preference_pkey
        update_columns: $updateColumns
      }
    ) {
      chat_id
      person_id
      notification_preference
      chat {
        id
        is_hidden
      }
    }
  }
`);

type ChatPreferences = {
  notificationPreference: ChatNotificationPreference;
  visibility: ChatVisibility;
};

type ChatNotificationPreference = 'inherit' | NotificationPreferenceChipValue;
type ChatVisibility = 'hidden' | 'visible';

const CHAT_VISIBILITY_OPTIONS: readonly ChipPreferenceOption<ChatVisibility>[] =
  [
    { icon: 'eye-outline', label: 'Visible', value: 'visible' },
    { icon: 'eye-off-outline', label: 'Hidden', value: 'hidden' },
  ];

export default function ChatDetailInfo() {
  const { dismiss } = useRouter();
  const { chatId } = useLocalSearchParams<{ chatId?: string }>();

  const [{ data, fetching, error }, refresh] = useQuery({
    query: GetChatDetailInfo,
    variables: { chatId: chatId || '' },
    pause: !chatId,
    requestPolicy: 'cache-and-network',
  });

  return (
    <>
      <Stack.Screen options={{ title: 'Chat Details' }} />
      <ContentBoundary
        data={data?.chat_by_pk}
        loading={fetching}
        error={error}
        onRetry={() => refresh({ requestPolicy: 'network-only' })}
        errorMessage="Could not load chat settings."
        empty={<Text>Chat settings are unavailable.</Text>}
      >
        {(chat) => (
          <ChatDetailInfoForm
            key={chat.id}
            accountDefault={
              data?.current_user?.chat_notification_preference ?? 'all'
            }
            chat={chat}
            closeChatDetails={() => dismiss()}
          />
        )}
      </ContentBoundary>
    </>
  );
}

function ChatDetailInfoForm({
  accountDefault,
  chat,
  closeChatDetails,
}: {
  accountDefault: NotificationPreferenceChipValue;
  chat: NonNullable<ResultOf<typeof GetChatDetailInfo>['chat_by_pk']>;
  closeChatDetails: () => void;
}) {
  const userId = useUserId();
  const chatId = chat.id;
  const headerLeft = useHeaderBackButton(closeChatDetails);
  const [, saveChatPreferences] = useMutation(SaveChatPreferences);
  const form = useForm<ChatPreferences>({
    defaultValues: {
      notificationPreference:
        chat.preference?.notification_preference ?? 'inherit',
      visibility: chat.is_hidden ? 'hidden' : 'visible',
    },
  });
  const {
    control,
    setValue,
    handleSubmit,
    setError,
    formState: { isDirty, isSubmitting, dirtyFields, errors },
  } = form;
  const notificationPreference = useWatch({
    control,
    name: 'notificationPreference',
  });
  const chatVisibility = useWatch({ control, name: 'visibility' });

  const chatType = chat?.chat_type || null;

  const chatTypeTitle =
    chatType === 'dm'
      ? 'Direct Message'
      : chatType === 'team'
        ? 'Team Chat'
        : 'Group Chat';

  const isGroup = chatType === 'group';
  const showParticipants = chat?.participants?.length;

  const title = chat?.metadata?.title || '';
  const color = chat?.metadata?.color;
  const avatarId = chat?.metadata?.avatar_id ?? undefined;
  const effectiveAccountNotificationPreference =
    chatType === 'dm' && accountDefault === 'mentions' ? 'all' : accountDefault;
  const notificationChipPreference: NotificationPreferenceChipValue =
    notificationPreference === 'inherit'
      ? effectiveAccountNotificationPreference
      : chatType === 'dm' && notificationPreference === 'mentions'
        ? 'all'
        : notificationPreference;
  const notificationPreferenceOptions = NOTIFICATION_PREFERENCE_OPTIONS.filter(
    (option) => chatType !== 'dm' || option.value !== 'mentions',
  ).map((option) => ({
    ...option,
    labelSuffix:
      option.value === effectiveAccountNotificationPreference
        ? '(default)'
        : undefined,
  }));
  const onNotificationPreferenceChange = useHaptics(
    (value: NotificationPreferenceChipValue) => {
      setValue(
        'notificationPreference',
        value === effectiveAccountNotificationPreference ? 'inherit' : value,
        { shouldDirty: true },
      );
    },
  );
  const onVisibilityChange = useHaptics((value: ChatVisibility) => {
    setValue('visibility', value, { shouldDirty: true });
  });
  const submitForm = async (values: ChatPreferences) => {
    const variables: VariablesOf<typeof SaveChatPreferences> = {
      object: { chat_id: chatId, person_id: userId },
      updateColumns: [],
    };
    if (dirtyFields.visibility) {
      variables.object.is_hidden = values.visibility === 'hidden';
      variables.updateColumns.push('is_hidden');
    }
    if (dirtyFields.notificationPreference) {
      variables.object.notification_preference =
        values.notificationPreference === 'inherit'
          ? null
          : values.notificationPreference;
      variables.updateColumns.push('notification_preference');
    }
    const result = await saveChatPreferences(variables).catch(() => null);
    if (result?.error || !result?.data?.insert_chat_person_preference_one) {
      setError('root', {
        message: 'Could not save chat settings. Please try again.',
      });
      return;
    }
    closeChatDetails();
  };

  return (
    <FormProvider {...form}>
      <Stack.Screen
        options={{
          title: 'Chat Details',
          headerShown: true,
          headerLeft,
        }}
      />

      <ScrollView style={styles.scrollView}>
        <Header.Toolbar>
          <Header.Row>
            <Avatar
              name={getAvatarText(title)}
              color={color}
              size="xl"
              id={avatarId}
            />
            <Box flex space="sm">
              <Box>
                <Header.Title testID="ChatDetailInfoTitle">
                  {title}
                </Header.Title>
                <Text variant="typographySecondary" size="sm">
                  {chatTypeTitle}
                </Text>
              </Box>
              <Box direction="horizontal" space="md" flexWrap="wrap">
                {isGroup ? (
                  <Link
                    href={{
                      pathname: './info/people',
                      params: { title },
                    }}
                    asChild
                  >
                    <ActionButton
                      icon="person-add-outline"
                      label="Add People"
                      variant="success"
                      disabled={isSubmitting}
                    />
                  </Link>
                ) : null}
                {isGroup ? (
                  <Link
                    href={{
                      pathname: './info/update',
                    }}
                    asChild
                  >
                    <ActionButton
                      icon="create-outline"
                      label="Edit Chat"
                      variant="secondary"
                      disabled={isSubmitting}
                    />
                  </Link>
                ) : null}
                {isGroup ? (
                  <Link
                    href={{
                      pathname: './info/leave',
                      params: { chatId, title },
                    }}
                    asChild
                  >
                    <ActionButton
                      testID="ChatDetailInfoLeaveButton"
                      icon="log-out-outline"
                      label="Leave Chat"
                      variant="error"
                      disabled={isSubmitting}
                    />
                  </Link>
                ) : null}
              </Box>
            </Box>
          </Header.Row>
        </Header.Toolbar>
        <Header.ColorBar backgroundColor={color} />

        {Platform.OS !== 'web' ? (
          <Box direction="vertical" pb="md">
            <ChipPreferenceRow
              label="Notifications"
              disabled={isSubmitting}
              onValueChange={onNotificationPreferenceChange}
              options={notificationPreferenceOptions}
              testIDPrefix="ChatDetailNotificationPreference"
              value={notificationChipPreference}
            />
          </Box>
        ) : null}

        <Box direction="vertical" pb="md">
          <ChipPreferenceRow
            disabled={isSubmitting}
            label="Visibility"
            onValueChange={onVisibilityChange}
            options={CHAT_VISIBILITY_OPTIONS}
            testIDPrefix="ChatDetailVisibility"
            value={chatVisibility}
          />
          <Box direction="vertical" px="xl">
            <Text variant="typographySecondary" size="sm">
              Hidden chats are removed from your chat list, and you will stop
              receiving notifications for them.
            </Text>
          </Box>
        </Box>

        {showParticipants ? (
          <Box px="xl" pt="lg" pb="md">
            <Text bold size="xl">
              {chat?.participants?.length} Participants
            </Text>
          </Box>
        ) : null}

        {showParticipants
          ? chat?.participants?.map((participant) => {
              const avatar = readFragment(
                PersonListItemAvatarFragment,
                participant,
              );
              const { name } = readFragment(
                PersonListItemNameFragment,
                participant,
              );

              return (
                <Row
                  key={participant.id}
                  testID={`ChatDetailInfoParticipant-${participant.id}`}
                >
                  <Avatar
                    color={avatar.color}
                    id={avatar.avatar_id || ''}
                    name={getAvatarText(avatar.name || '')}
                    size="md"
                  />
                  <Text flexShrink={1} numberOfLines={1} size="xl">
                    {name}
                  </Text>
                </Row>
              );
            })
          : null}
      </ScrollView>
      <ModalKeyboardStickyView>
        {errors.root?.message ? (
          <Box px="xl" py="md">
            <Text variant="error" accessibilityRole="alert">
              {errors.root.message}
            </Text>
          </Box>
        ) : null}
        <FormSaveButton
          info={{ text: 'Save' }}
          isDisabled={!isDirty || isSubmitting}
          onPress={handleSubmit(submitForm)}
        />
      </ModalKeyboardStickyView>
    </FormProvider>
  );
}

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
  },
});
